import type { Transition, Variants } from "framer-motion";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "next/router";
import type { NextPage } from "next/types";
import crowd from "public/images/RelentlessCrowd.png";
import { useEffect, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  NavTab,
  SplashImage,
  useBreakpoints,
} from "@sorcery/ui";

import { Page } from "~/components/layouts/Page";
import { FeatureTitle } from "~/components/layouts/titles/FeatureTitle";
import { RetailerTabEnum } from "~/types/retailers";
import { BecomeARetailerDistributorsTab } from "./tabs/BecomeARetailerDistributorsTab";
import { BecomeARetailerEventsTab } from "./tabs/BecomeARetailerEventsTab";
import { BecomeARetailerPoliciesTab } from "./tabs/BecomeARetailerPoliciesTab";
import { BecomeARetailerProductTab } from "./tabs/BecomeARetailerProductTab";

const variants: Variants = {
  enter: { x: 1000, opacity: 0 },
  center: { zIndex: 1, x: 0, opacity: 1 },
  exit: {
    zIndex: 0,
    x: -1000,
    opacity: 0,
  },
};

const transition: Transition = {
  x: {
    type: "easeOut",
    stiffness: 300,
    damping: 30,
    duration: 0.15,
  },
  opacity: { duration: 0.1 },
};

const BecomeARetailer: NextPage = () => {
  const { isReady, query: routerParams, replace } = useRouter();

  const { isLg } = useBreakpoints();
  const tab = routerParams.tab as RetailerTabEnum | undefined;
  // dont set default tab on mobile
  const [activeTab, setActiveTab] = useState((tab ?? isLg) ? RetailerTabEnum.PRODUCT : undefined);
  const showProductsTab = activeTab === RetailerTabEnum.PRODUCT;
  const showDistributorsTab = activeTab === RetailerTabEnum.DISTRIBUTORS;
  const showEventsTab = activeTab === RetailerTabEnum.EVENTS;
  const showPoliciesTab = activeTab === RetailerTabEnum.POLICIES;

  // Track tab when router is loaded
  useEffect(() => {
    if (isReady) {
      if (tab && Object.values(RetailerTabEnum).includes(tab)) {
        setActiveTab(tab);
      } else {
        // dont set default tab on mobile
        setActiveTab(isLg ? RetailerTabEnum.PRODUCT : undefined);
      }
    }
  }, [tab, isReady, isLg]);

  return (
    <Page
      title="Sorcery | Become a Retailer"
      description="Become a Sorcery retailer."
      image={crowd.src}
      hero={<SplashImage src={crowd} circle />}
    >
      <FeatureTitle
        title="Become A Retailer"
        orientation="center"
        className="!text-3xl xs:!text-[2rem] sm:!text-4xl md:!text-6xl"
      />

      {/* Content */}
      <section className="sorcery-wide-container sorcery-section !mt-0 !py-0 lg:!mt-12">
        {/* Small screen nav */}
        <div className="-mt-6 mb-24 lg:hidden">
          {!tab ? (
            <Accordion type="single" className="border-t border-t-line-color">
              {/* selector */}
              {Object.values(RetailerTabEnum).map((t) => (
                <AccordionItem value={t} key={t}>
                  <AccordionTrigger
                    className="pl-3 pr-1 font-title font-normal capitalize"
                    onClick={() =>
                      void replace(
                        {
                          query: {
                            ...routerParams,
                            tab: t,
                          },
                        },
                        undefined,
                        {
                          shallow: true,
                        },
                      )
                    }
                  >
                    {t}
                  </AccordionTrigger>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <Accordion type="single" className="border-t border-t-line-color" defaultValue="back">
              {/* back button */}
              <AccordionItem value={"back"} key={"back"}>
                <AccordionTrigger
                  className="flex items-center justify-center gap-2 pl-3 font-title font-normal"
                  chevron={false}
                  onClick={() =>
                    void replace(
                      {
                        query: {
                          ...routerParams,
                          tab: undefined,
                        },
                      },
                      undefined,
                      {
                        shallow: true,
                      },
                    )
                  }
                >
                  <BsArrowLeft className="-mt-0.5 inline h-5 w-5" />
                  <span>Back to Menu</span>
                  <div className="h-4 w-4" />
                </AccordionTrigger>
              </AccordionItem>
            </Accordion>
          )}
        </div>

        {/* Big screen nav */}
        <div className="flex flex-row animate-in fade-in">
          <div className="mr-6 hidden w-[190px] min-w-[190px] flex-col border-r border-line-color pr-6 lg:flex">
            <div className="sticky top-6 flex flex-col font-title">
              {Object.values(RetailerTabEnum).map((t) => (
                <NavTab
                  key={t}
                  title={t}
                  onSelect={() =>
                    void replace(
                      {
                        query: {
                          ...routerParams,
                          tab: t,
                        },
                      },
                      undefined,
                      {
                        shallow: true,
                      },
                    )
                  }
                  active={activeTab === t}
                />
              ))}
            </div>
          </div>

          {/* Main section */}
          <div className="grow overflow-hidden sm:block">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial="enter"
                animate="center"
                exit="exit"
                variants={variants}
                transition={transition}
              >
                {showProductsTab && <BecomeARetailerProductTab />}
                {showDistributorsTab && <BecomeARetailerDistributorsTab />}
                {showEventsTab && <BecomeARetailerEventsTab />}
                {showPoliciesTab && <BecomeARetailerPoliciesTab />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>
    </Page>
  );
};

export default BecomeARetailer;
